// src/hooks/mediaConversion/useMediaConversionJobs.ts

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  MediaConversionJobDTO,
  MediaJobTarget,
} from "@/types/mediaConversion/mediaJob";
import { getJobsForTarget } from "@/services/mediaConversion/mediaConversionApi";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type Options = {
  enabled?: boolean;
  fieldName?: string;

  // Polling
  pollMs?: number;     // default 2000
  maxPollMs?: number;  // default 6000

  // Stop rules
  stopWhenDone?: boolean; // default true
};

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

// True if any job is queued or processing
function hasActiveJobs(data: MediaConversionJobDTO[]) {
  return data.some(
    (j) => j.status === "queued" || j.status === "processing"
  );
}

/* ------------------------------------------------------------------ */
/* Hook                                                               */
/* ------------------------------------------------------------------ */

export function useMediaConversionJobs(
  target: MediaJobTarget | null,
  opts?: Options
) {
  const enabled = opts?.enabled ?? true;
  const fieldName = opts?.fieldName;
  const stopWhenDone = opts?.stopWhenDone ?? true;

  const basePoll = opts?.pollMs ?? 2000;
  const maxPoll = opts?.maxPollMs ?? 6000;

  const [jobs, setJobs] = useState<MediaConversionJobDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ------------------------------------------------------------------
   * Refs (no re-render)
   * ------------------------------------------------------------------ */

  const pollMsRef = useRef<number>(basePoll);
  const timerRef = useRef<number | null>(null);

  const aliveRef = useRef<boolean>(true);
  const stoppedRef = useRef<boolean>(false);

  // ✅ Prevent early stop before any job appears
  const seenAnyJobRef = useRef<boolean>(false);

  /* ------------------------------------------------------------------
   * Stable effect key
   * ------------------------------------------------------------------ */

  const stableKey = useMemo(() => {
    if (!target) return "none";
    return JSON.stringify({
      target,
      fieldName: fieldName ?? null,
      basePoll,
      maxPoll,
      stopWhenDone,
      enabled,
    });
  }, [target, fieldName, basePoll, maxPoll, stopWhenDone, enabled]);

  /* ------------------------------------------------------------------
   * Lifecycle safety
   * ------------------------------------------------------------------ */

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  /* ------------------------------------------------------------------
   * Polling effect
   * ------------------------------------------------------------------ */

  useEffect(() => {
    // Reset state on key change
    setJobs([]);
    setError(null);
    setLoading(false);

    pollMsRef.current = basePoll;
    stoppedRef.current = false;
    seenAnyJobRef.current = false;

    if (!enabled || !target) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled || !aliveRef.current || stoppedRef.current) return;

      // Show spinner only until first response
      setLoading((prev) => prev || jobs.length === 0);

      try {
        const data = await getJobsForTarget(target, { fieldName });
        if (cancelled || !aliveRef.current || stoppedRef.current) return;

        setJobs(data);
        setError(null);

        // Mark that at least one job has appeared
        if (data.length > 0) {
          seenAnyJobRef.current = true;
        }

        /* --------------------------------------------------
         * STOP RULE (race-safe)
         * Stop ONLY after we have seen jobs before
         * and now none are active anymore.
         * -------------------------------------------------- */
        if (
          stopWhenDone &&
          seenAnyJobRef.current &&
          !hasActiveJobs(data)
        ) {
          stoppedRef.current = true;

          if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
          }

          setLoading(false);
          return;
        }

        // Fast polling while expecting activity
        pollMsRef.current = basePoll;

      } catch (e: any) {
        if (cancelled || !aliveRef.current || stoppedRef.current) return;

        // Backoff on errors
        pollMsRef.current = Math.min(
          maxPoll,
          pollMsRef.current + 1000
        );

        setError(e?.message || "Failed to fetch conversion status");
      } finally {
        if (cancelled || !aliveRef.current || stoppedRef.current) return;

        setLoading(false);
        timerRef.current = window.setTimeout(
          tick,
          pollMsRef.current
        );
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  return { jobs, loading, error };
}
